import React, { useMemo, useState } from "react";
import { Button, Drawer, Space, Tag, Typography, Table, Alert, message } from "antd";

const { Title, Text } = Typography;

function sym(cur) {
  if (cur === "INR") return "₹";
  if (cur === "GBP") return "£";
  return "";
}

export default function ChangesReviewDrawer({
  open,
  onClose,
  draft = {},
  grid,
  currency = "INR",
  onConfirmSave,
}) {
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => {
    const out = [];
    const roomsById = new Map();

    (grid?.rooms || []).forEach((r) => roomsById.set(String(r.pms_room_type_id), r));

    // rate plan map
    const ratePlanTitle = new Map();
    (grid?.rooms || []).forEach((r) => {
      (r.ratePlans || []).forEach((rp) => {
        ratePlanTitle.set(String(rp.id), `${r.name} → ${rp.title}`);
      });
    });

    for (const [k, v] of Object.entries(draft)) {
      const parts = k.split("|");

      if (parts[0] === "A") {
        const pmsRoomId = parts[1];
        const date = parts[2];
        const room = roomsById.get(String(pmsRoomId));
        out.push({
          key: k,
          type: "Availability",
          room: room?.name || `Room ${pmsRoomId}`,
          ratePlan: "—",
          date,
          value: Number(v),
          display: String(v),
        });
      }

      if (parts[0] === "R") {
        const ratePlanId = parts[1];
        const occ = parts[2];
        const date = parts[3];
        out.push({
          key: k,
          type: "Rate",
          room: ratePlanTitle.get(String(ratePlanId))?.split(" → ")[0] || "—",
          ratePlan: ratePlanTitle.get(String(ratePlanId)) || `RatePlan ${ratePlanId}`,
          date,
          value: Number(v),
          display: `${sym(currency)}${v} (Occ ${occ})`,
        });
      }
    }

    // Sort by date then type
    out.sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : a.type.localeCompare(b.type)));
    return out;
  }, [draft, grid, currency]);

  const summary = useMemo(() => {
    let avl = 0;
    let rate = 0;
    rows.forEach((r) => {
      if (r.type === "Availability") avl++;
      if (r.type === "Rate") rate++;
    });
    return { avl, rate, total: rows.length };
  }, [rows]);

  const columns = [
    { title: "Type", dataIndex: "type", width: 120, render: (t) => <Tag>{t}</Tag> },
    { title: "Room", dataIndex: "room", width: 180 },
    { title: "Rate Plan", dataIndex: "ratePlan" },
    { title: "Date", dataIndex: "date", width: 120 },
    { title: "New Value", dataIndex: "display", width: 160 },
  ];

  async function confirmSave() {
    try {
      setSaving(true);
      await onConfirmSave?.();
      message.success(`Saved: ${summary.total} changes`);
      onClose?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>Review Changes</Title>
          <Text type="secondary">Confirm your updates before pushing to channels.</Text>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={onClose}>Back</Button>
          <Space>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(draft, null, 2));
                message.success("Changes copied.");
              }}
            >
              Copy JSON
            </Button>
            <Button type="primary" loading={saving} disabled={!rows.length} onClick={confirmSave}>
              Confirm & Save
            </Button>
          </Space>
        </div>
      }
    >
      {!rows.length ? (
        <Alert type="info" showIcon message="No pending changes." />
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <Tag color="gold">Total: {summary.total}</Tag>
            <Tag color="blue">Availability: {summary.avl}</Tag>
            <Tag color="green">Rates: {summary.rate}</Tag>
            <Tag>{currency}</Tag>
          </div>

          <Table
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={{ pageSize: 12 }}
            sticky
          />
        </>
      )}
    </Drawer>
  );
}
